import { ReactNode } from "react";
import { useIntl } from "react-intl";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

interface Props {
  label: string;
  children: ReactNode;
  variant?: "section" | "subSection";
  actions?: ReactNode;
}

export default function AccordionSection({
  label,
  children,
  variant = "section",
  actions,
}: Props) {
  const intl = useIntl();
  const isSubSection = variant === "subSection";

  return (
    <Accordion
      defaultExpanded
      disableGutters
      sx={(theme) => ({
        border: `1px solid ${theme.vars.palette.divider}`,
        borderRadius: isSubSection ? "4px !important" : "6px !important",
        mb: isSubSection ? "8px" : "16px",
        ...(isSubSection && { mx: "16px" }),
        boxShadow: "unset",
        overflow: "hidden",
        "&:before": { display: "none" },
      })}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "text.contrastPrimary" }} />}
        sx={{
          minHeight: "unset",
          padding: isSubSection ? "0 12px" : "0 16px",
          color: "text.contrastPrimary",
          backgroundColor: "background.inverted",
          "&.Mui-expanded": { minHeight: "unset" },
          "& .MuiAccordionSummary-content": {
            margin: isSubSection ? "6px 0" : "8px 0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          },
        }}
      >
        {isSubSection ? (
          <Typography variant="caption" sx={{ fontWeight: "bold" }}>
            {intl.formatMessage({ id: label })}
          </Typography>
        ) : (
          <Typography
            component="h2"
            sx={{ fontWeight: "bold", fontSize: "1rem" }}
          >
            {intl.formatMessage({ id: label })}
          </Typography>
        )}
        {actions && (
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              marginRight: 1,
            }}
          >
            {actions}
          </Box>
        )}
      </AccordionSummary>
      <AccordionDetails
        sx={(theme) =>
          isSubSection
            ? { p: 0, paddingBottom: "6px" }
            : {
                padding: "8px 0",
                borderTop: `1px solid ${theme.vars.palette.divider}`,
              }
        }
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
}
